import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import { ChevronDown, ChevronRight, LogOut, Menu, X, Bell, Users, Sparkles, Video, Globe } from 'lucide-react';
import SignupPage from '../auth/SignupPage';
import SigninPage from '../auth/SigninPage';
import ProfileCircle from './ProfileCircle';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectUserDetails } from '../../store/slices/userSlice';
import storageHelper from '../../utils/storageHelper';
import Modal, { Button } from '../common/modal';
import ForgotPassword from '../auth/ForgetPassword';

const CONSTANTS = {
  SCROLL: { THRESHOLD: 50, DEBOUNCE: 10 },
  DROPDOWN_DELAY: 100,
  STORAGE_KEYS: {
    AUTH: 'authToken',
    AFFILIATE: 'affiliateId'
  }
};

const LogoutModal = ({ isOpen, onClose, onConfirm }) => (
  <Modal isOpen={isOpen} onClose={onClose}>
    <div className="text-center">
      <LogOut className="w-12 h-12 mx-auto text-gray-400 mb-4" />
      <h2 className="text-xl font-semibold mb-4">Confirm Logout</h2>
      <p className="text-gray-600 mb-6">Are you sure you want to logout?</p>
      <div className="flex justify-center space-x-4">
        <Button className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg" onClick={onClose}>
          Cancel
        </Button>
        <Button className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg" onClick={onConfirm}>
          Logout
        </Button>
      </div>
    </div>
  </Modal>
);

const NewFeatureBanner = ({ isVisible, onDismiss, navigate }) => {
  if (!isVisible) return null;

  return (
    <div
      className="fixed w-full z-[101] bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 text-white py-2 px-4 overflow-hidden flex flex-row gap-2 sm:gap-x-8 justify-center items-center"
      style={{ top: '65px' }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-2">
        <div className="flex items-center sm:space-x-2">
          <span className="bg-yellow-400 text-purple-800 px-2 py-0.5 rounded-full text-xs font-bold animate-pulse">NEW</span>
          <span className="font-bold max-sm:text-xs text-base">🌐 WebBuilder Now Available! </span>
        </div>

        <span className="max-sm:text-xs text-sm max-sm:hidden sm:inline">
          Create and launch professional, customized websites in minutes with our AI-powered website builder.
        </span>
        <span className="text-purple-100 text-sm hidden sm:block">.</span>
      </div>
      <div className="flex items-center sm:space-x-2">
        <Button
          onClick={() => navigate('/webbuilder')}
          className="bg-white text-purple-700 hover:bg-purple-50 px-3 py-1.5 rounded-lg font-semibold text-sm transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105 flex items-center gap-x-2"
        >
          <Globe className="w-4 h-4 sm:mr-1 max-sm:hidden" />
          <span className="hidden sm:inline">Try Now</span>
          <span className="sm:hidden">Try</span>
        </Button>
        <button
          onClick={onDismiss}
          className="text-purple-200 hover:text-white p-1 rounded-full hover:bg-purple-600 transition-colors"
          aria-label="Dismiss notification"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Navbar = () => {
  const isHomePage = useLocation().pathname === '/';
  const _navigate = useNavigate();
  const navigate = useCallback(
    (path, options) => {
      startTransition(() => {
        _navigate(path, options);
      });
    },
    [_navigate]
  );
  const dispatch = useDispatch();
  const [state, setState] = useState({
    showSignin: false,
    showSignup: false,
    showLogoutModal: false,
    scrolled: false,
    isAuthenticated: false,
    activeDropdown: null,
    activeSubmenu: null,
    activeSubSubmenu: null,
    userData: null,
    isLoading: true,
    mobileMenuOpen: false,
    showNewFeatureBanner: true
  });

  const updateState = useCallback((updates) => setState((prev) => ({ ...prev, ...updates })), []);
  const userDetails = useSelector(selectUserDetails);

  // Authentication effect
  useEffect(() => {
    try {
      const token = storageHelper.get(CONSTANTS.STORAGE_KEYS.AUTH);

      updateState({
        isAuthenticated: !!token,
        isLoading: false
      });
    } catch (error) {
      console.error('Auth Error:', error);
      updateState({ isLoading: false });
    }
  }, [updateState]);

  // Scroll effect with debounce
  useEffect(() => {
    let timeoutId;
    const handleScroll = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => updateState({ scrolled: window.scrollY > CONSTANTS.SCROLL.THRESHOLD }), CONSTANTS.SCROLL.DEBOUNCE);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, [updateState]);
  const isAdmin = state.isAuthenticated && userDetails?.role?.toLowerCase() === 'admin';

  // const menuItems = {
  //   Features: [
  //     { label: 'Dashboard', path: '/dashboard', isDisabled: false },
  //     { label: 'Community', path: `/CommunityPage?userId=${userDetails?.id || ''}`, isDisabled: false },
  //     { label: 'Investment Guidance Center', path: '/investment-guidance', isDisabled: false },
  //     {
  //       label: 'Express Center',
  //       subItems: [
  //         {
  //           label: 'Business Idea Explorer',
  //           path: '/business-ideas',
  //           isDisabled: userDetails?.subscription_status === 'free'
  //         },
  //         {
  //           label: 'Generators',
  //           subItems: [
  //             {
  //               label: 'Business Plans Generator',
  //               path: '/business-plan-generator',
  //               isDisabled: userDetails?.subscription_status === 'free'
  //             },
  //             {
  //               label: 'Business Idea Generator',
  //               path: '/business-ideas',
  //               isDisabled: userDetails?.subscription_status === 'free'
  //             },
  //             {
  //               label: 'Idea Development Engine',
  //               path: '/BusinessIdeaDevelopmentEngine',
  //               isDisabled: userDetails?.subscription_status === 'free'
  //             },
  //             {
  //               label: 'Side Hustle Generator',
  //               path: '/extra-income',
  //               isDisabled: userDetails?.subscription_status === 'free' || userDetails?.subscription_status === 'basic'
  //             }
  //           ]
  //         },
  //         {
  //           label: 'Analyzers',
  //           subItems: [
  //             {
  //               label: 'Business Idea Analyzer',
  //               path: '/business-idea-analyzer',
  //               isDisabled: userDetails?.subscription_status === 'free' || userDetails?.subscription_status === 'basic'
  //             }
  //           ]
  //         },
  //         {
  //           label: 'Simulators',
  //           subItems: [
  //             {
  //               label: 'Business Scenario Simulator',
  //               path: '/business-scenario-simulator',
  //               isDisabled: userDetails?.subscription_status === 'free' || userDetails?.subscription_status === 'basic'
  //             }
  //           ]
  //         },
  //         {
  //           label: 'Networks',
  //           subItems: [
  //             {
  //               label: 'AI Expert Network',
  //               path: '/expert-network',
  //               isDisabled:
  //                 userDetails?.subscription_status === 'free' ||
  //                 userDetails?.subscription_status === 'basic' ||
  //                 userDetails?.subscription_status === 'pro'
  //             },
  //             {
  //               label: 'Industry Expert Advisor Hub',
  //               path: '/industry-expert-advisor-hub',
  //               isDisabled:
  //                 userDetails?.subscription_status === 'free' ||
  //                 userDetails?.subscription_status === 'basic' ||
  //                 userDetails?.subscription_status === 'pro'
  //             }
  //           ]
  //         }
  //       ]
  //     }
  //   ],
  //   Resources: [
  //     {
  //       label: 'Learning Center',
  //       path: '/learning',
  //       isDisabled: false
  //     }
  //   ],
  //   ...(isAdmin && {
  //     Admin: [
  //       {
  //         label: 'Affiliate Console',
  //         path: '/AffiliateConsole',
  //         isDisabled: false
  //       },
  //       {
  //         label: 'Blog Admin',
  //         path: '/BlogManagementDashboard',
  //         isDisabled: false
  //       }
  //     ]
  //   })
  // };
  // const menuItems = {
  //   ...((userDetails?.subscription_status !== 'free' || userDetails?.role?.toLowerCase() === 'admin') && {
  //     Features: [
  //       { label: 'Dashboard', path: '/dashboard', isDisabled: false },
  //       { label: 'Community', path: `/CommunityPage?userId=${userDetails?.id || ''}`, isDisabled: false },
  //       { label: 'Investment Guidance Center', path: '/investment-guidance', isDisabled: false },
  //       { label: 'PathFinder Videos', path: '/PathFinderVideos', isDisabled: false },
  //       { label: 'Global Translator', path: '/PathFinderGlobalTranslator', isDisabled: false },
  //       {
  //         label: 'Express Center',
  //         subItems: [
  //           {
  //             label: 'Business Idea Explorer',
  //             path: '/business-ideas',
  //             isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
  //           },
  //           {
  //             label: 'Generators',
  //             subItems: [
  //               {
  //                 label: 'Business Plans Generator',
  //                 path: '/business-plan-generator',
  //                 isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
  //               },
  //               {
  //                 label: 'Business Idea Generator',
  //                 path: '/business-ideas',
  //                 isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
  //               },
  //               {
  //                 label: 'Idea Development Engine',
  //                 path: '/BusinessIdeaDevelopmentEngine',
  //                 isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
  //               },
  //               {
  //                 label: 'Side Hustle Generator',
  //                 path: '/extra-income',
  //                 isDisabled:
  //                   (userDetails?.subscription_status === 'free' ||
  //                     userDetails?.subscription_status === 'basic' ||
  //                     userDetails?.subscription_status === 'pro') &&
  //                   userDetails?.role?.toLowerCase() !== 'admin'
  //               }
  //             ]
  //           },
  //           {
  //             label: 'Analyzers',
  //             subItems: [
  //               {
  //                 label: 'Business Idea Analyzer',
  //                 path: '/business-idea-analyzer',
  //                 isDisabled:
  //                   (userDetails?.subscription_status === 'free' || userDetails?.subscription_status === 'basic') &&
  //                   userDetails?.role?.toLowerCase() !== 'admin'
  //               }
  //             ]
  //           },
  //           {
  //             label: 'Simulators',
  //             subItems: [
  //               {
  //                 label: 'Business Scenario Simulator',
  //                 path: '/business-scenario-simulator',
  //                 isDisabled:
  //                   (userDetails?.subscription_status === 'free' || userDetails?.subscription_status === 'basic') &&
  //                   userDetails?.role?.toLowerCase() !== 'admin'
  //               }
  //             ]
  //           },
  //           {
  //             label: 'Networks',
  //             subItems: [
  //               {
  //                 label: 'AI Expert Network',
  //                 path: '/expert-network',
  //                 isDisabled:
  //                   (userDetails?.subscription_status === 'free' ||
  //                     userDetails?.subscription_status === 'basic' ||
  //                     userDetails?.subscription_status === 'pro') &&
  //                   userDetails?.role?.toLowerCase() !== 'admin'
  //               },
  //               {
  //                 label: 'Industry Expert Advisor Hub',
  //                 path: '/industry-expert-advisor-hub',
  //                 isDisabled:
  //                   (userDetails?.subscription_status === 'free' ||
  //                     userDetails?.subscription_status === 'basic' ||
  //                     userDetails?.subscription_status === 'pro') &&
  //                   userDetails?.role?.toLowerCase() !== 'admin'
  //               }
  //             ]
  //           }
  //         ]
  //       }
  //     ]
  //   }),

  //   ...(userDetails?.subscription_status !== 'free' && {
  //     Resources: [
  //       {
  //         label: 'Learning Center',
  //         path: '/learning',
  //         isDisabled: false
  //       }
  //     ]
  //   }),

  //   ...(isAdmin && {
  //     Admin: [
  //       {
  //         label: 'Affiliate Console',
  //         path: '/AffiliateConsole',
  //         isDisabled: false
  //       },

  //       {
  //         label: 'Blog Admin',
  //         path: '/BlogManagementDashboard',
  //         isDisabled: false
  //       },
  //       {
  //         label: 'Campaign Management',
  //         path: '/CampaignManagementSystem',
  //         isDisabled: false
  //       },
  //       {
  //         label: 'Feature Analytics',
  //         path: '/PathFinderFeatureAnalytics',
  //         isDisabled: false
  //       },
  //       {
  //         label: 'PathFinder Analytics',
  //         path: '/PathFinderAnalytics',
  //         isDisabled: false
  //       },
  //       {
  //         label: 'Management Console',
  //         path: '/ManagementConsole',
  //         isDisabled: false
  //       },
  //       {
  //         label: 'Support Admin',
  //         path: '/SupportManagementPortal',
  //         isDisabled: false
  //       }
  //     ]
  //   })
  // };
  const menuItems = {
    ...((userDetails?.subscription_status !== 'free' || userDetails?.role?.toLowerCase() === 'admin') && {
      Features: [
        { label: 'Community', path: `/CommunityPage?userId=${userDetails?.id || ''}`, isDisabled: false },
        { label: 'Business In A Box', path: '/BusinessInABox', isDisabled: false },
        { label: 'Dashboard', path: '/dashboard', isDisabled: false },
        { label: 'Global Language Translator', path: '/PathFinderGlobalTranslator', isDisabled: false },
        { label: 'Investment Guidance Center', path: '/investment-guidance', isDisabled: false },
        { label: 'Video Tutorials', path: '/PathFinderInfo', isDisabled: false },
        { label: 'PathFinder Multi-AI Search', path: '/PathfinderSynthesizedSearch', isDisabled: false },
        { label: 'PathFinder Videos', path: '/PathFinderVideos', isDisabled: false },
        { label: 'PathFinder Project Management', path: '/PathFinderProjectManagement', isDisabled: false },
        { label: 'PathFinder Connect', path: '/PathFinderConnect', isDisabled: false },
        { label: 'PathFinder Idea Exchange', path: '/PathFinderIdeaExchange', isDisabled: false },
        { label: 'WebBuilder', path: '/WebBuilder', isDisabled: false },
        { label: 'Landing Page Builder', path: '/LandingPageBuilder', isDisabled: false },
        // { label: 'WebBuilder Sales Onboarding', path: '/WebBuilderSalesOnboarding', isDisabled: false },
        {
          label: 'Express Center',
          subItems: [
            // ✅ Business Idea Explorer
            {
              label: 'Business Idea Explorer',
              path: '/business-ideas',
              isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
            },

            // ✅ Generators (sorted inside)
            {
              label: 'Generators',
              subItems: [
                {
                  label: 'Business Idea Generator',
                  path: '/business-ideas',
                  isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
                },
                {
                  label: 'Business Plans Generator',
                  path: '/business-plan-generator',
                  isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
                },
                {
                  label: 'Idea Development Engine',
                  path: '/BusinessIdeaDevelopmentEngine',
                  isDisabled: userDetails?.subscription_status === 'free' && userDetails?.role?.toLowerCase() !== 'admin'
                },
                {
                  label: 'LinkedIn Profile Generator',
                  path: '/LinkedInProfileGenerator'
                },
                {
                  label: 'Side Hustle Generator',
                  path: '/extra-income',
                  isDisabled:
                    (userDetails?.subscription_status === 'free' ||
                      userDetails?.subscription_status === 'basic' ||
                      userDetails?.subscription_status === 'pro') &&
                    userDetails?.role?.toLowerCase() !== 'admin'
                }
              ]
            },

            // ✅ Analyzers
            {
              label: 'Analyzers',
              subItems: [
                {
                  label: 'Business Idea Analyzer',
                  path: '/business-idea-analyzer',
                  isDisabled:
                    (userDetails?.subscription_status === 'free' || userDetails?.subscription_status === 'basic') &&
                    userDetails?.role?.toLowerCase() !== 'admin'
                }
              ]
            },

            // ✅ Networks
            {
              label: 'Networks',
              subItems: [
                {
                  label: 'AI Expert Network',
                  path: '/expert-network',
                  isDisabled:
                    (userDetails?.subscription_status === 'free' ||
                      userDetails?.subscription_status === 'basic' ||
                      userDetails?.subscription_status === 'pro') &&
                    userDetails?.role?.toLowerCase() !== 'admin'
                },
                {
                  label: 'Industry Expert Advisor Hub',
                  path: '/industry-expert-advisor-hub',
                  isDisabled:
                    (userDetails?.subscription_status === 'free' ||
                      userDetails?.subscription_status === 'basic' ||
                      userDetails?.subscription_status === 'pro') &&
                    userDetails?.role?.toLowerCase() !== 'admin'
                }
              ]
            },

            // ✅ Simulators
            {
              label: 'Simulators',
              subItems: [
                {
                  label: 'Business Scenario Simulator',
                  path: '/business-scenario-simulator',
                  isDisabled:
                    (userDetails?.subscription_status === 'free' || userDetails?.subscription_status === 'basic') &&
                    userDetails?.role?.toLowerCase() !== 'admin'
                }
              ]
            }
          ]
        }
      ]
    }),

    ...(userDetails?.subscription_status !== 'free' && {
      Resources: [
        {
          label: 'Learning Center',
          path: '/learning',
          isDisabled: false
        }
      ]
    }),

    ...(isAdmin && {
      Admin: [
        {
          label: 'Affiliate Console',
          path: '/AffiliateConsole',
          isDisabled: false
        },

        {
          label: 'Blog Admin',
          path: '/BlogManagementDashboard',
          isDisabled: false
        },
        {
          label: 'Campaign Management',
          path: '/CampaignManagementSystem',
          isDisabled: false
        },
        {
          label: 'Feature Analytics',
          path: '/PathFinderFeatureAnalytics',
          isDisabled: false
        },
        {
          label: 'Feature Management Console',
          path: '/FeatureManagementConsole',
          isDisabled: false
        },
        {
          label: 'PathFinder Analytics',
          path: '/PathFinderAnalytics',
          isDisabled: false
        },
        {
          label: 'Video Library Admin',
          path: '/PathFinderInfoAdmin',
          isDisabled: false
        },
        {
          label: 'PathFinder Newsletter',
          path: '/PathFinderNewsletter',
          isDisabled: false
        },
        {
          label: 'Management Console',
          path: '/ManagementConsole',
          isDisabled: false
        },
        {
          label: 'Web Builder Onboarding Review',
          path: '/WebBuilderOnboardingReview',
          isDisabled: false
        },
        {
          label: 'Web BuilderPost Demo Admin',
          path: '/WebBuilderPostDemoAdmin',
          isDisabled: false
        },
        {
          label: 'Support Admin',
          path: '/SupportManagementPortal',
          isDisabled: false
        },
        {
          label: 'Payroll Report',
          path: '/WebBuilderPayrollReport',
          isDisabled: false
        }
      ]
    })
  };

  const handlers = useMemo(
    () => ({
      submenuHover: (menuTitle) =>
        updateState({
          activeDropdown: menuTitle,
          activeSubmenu: null,
          activeSubSubmenu: null
        }),

      subSubmenuHover: (submenuTitle) =>
        updateState({
          activeSubSubmenu: submenuTitle
        }),

      closeDropdown: (dropdown) => {
        const timer = setTimeout(
          () =>
            updateState({
              activeDropdown: null,
              ...(dropdown === 'Features' && {
                activeSubmenu: null,
                activeSubSubmenu: null
              })
            }),
          CONSTANTS.DROPDOWN_DELAY
        );
        return () => clearTimeout(timer);
      },

      logout: async () => {
        try {
          storageHelper.remove(CONSTANTS.STORAGE_KEYS.AUTH);
          storageHelper.remove(CONSTANTS.STORAGE_KEYS.AFFILIATE);
          storageHelper.clear();
          sessionStorage.removeItem('hasSeenTrialModal');
          sessionStorage.removeItem('affiliateFormData');
          sessionStorage.removeItem('pathfinder_session_id');
          dispatch(logout());
          updateState({
            isAuthenticated: false,
            userData: null,
            showLogoutModal: false
          });
          navigate('/');
        } catch (error) {
          console.error('Logout Error:', error);
        }
      },

      toggleModal: (modalType, value) => updateState({ [modalType]: value }),

      dismissFeatureBanner: () => {
        updateState({ showNewFeatureBanner: false });
      }
    }),
    [navigate, updateState, dispatch]
  );

  if (state.isLoading) return <div className="h-[80px]" />;

  const renderSubMenu = (items, level = 0) => (
    <div
      className={`absolute ${
        level === 0 ? 'left-0 mt-0' : 'left-full top-0'
      } w-64 bg-purple-200 border border-purple-300 rounded-lg shadow-lg`}
    >
      {items.map((item) => (
        <div
          key={item.label}
          className="relative group"
          onMouseEnter={() => {
            if (item.subItems) {
              updateState({ activeSubmenu: item.label });
            }
          }}
        >
          <Button
            onClick={() => {
              if (!state.isAuthenticated) {
                handlers.toggleModal('showSignup', true);
              } else if (!item.isDisabled) {
                navigate(item.path);
              }
            }}
            className={`w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-300 hover:text-purple-900 flex items-center justify-between
              ${!state.isAuthenticated || item.isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {item.label}
            {item.subItems && <ChevronRight className="h-4 w-4" />}
          </Button>
          {item.subItems && (
            <div
              className={`${
                state.activeSubmenu === item.label ? 'block' : 'hidden'
              } absolute left-full top-0 w-64 bg-purple-200 border border-purple-300 rounded-lg shadow-lg`}
            >
              {item.subItems.map((subItem) => (
                <div
                  key={subItem.label}
                  className="relative group"
                  onMouseEnter={() => {
                    if (subItem.subItems) {
                      updateState({ activeSubSubmenu: subItem.label });
                    }
                  }}
                >
                  <button
                    onClick={() => !subItem.subItems && (!state.isAuthenticated || subItem.isDisabled ? null : navigate(subItem.path))}
                    className={`w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-300 hover:text-purple-900 flex items-center justify-between
                      ${!state.isAuthenticated || subItem.isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {subItem.label}
                    {subItem.subItems && <ChevronRight className="h-4 w-4" />}
                  </button>
                  {subItem.subItems && (
                    <div
                      className={`${
                        state.activeSubSubmenu === subItem.label ? 'block' : 'hidden'
                      } absolute left-full top-0 w-64 bg-purple-200 border border-purple-300 rounded-lg shadow-lg`}
                    >
                      {subItem.subItems.map((subSubItem) => (
                        <button
                          key={subSubItem.label}
                          onClick={() => (!state.isAuthenticated || subSubItem.isDisabled ? null : navigate(subSubItem.path))}
                          className={`w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-300 hover:text-purple-900
                            ${!state.isAuthenticated || subSubItem.isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {subSubItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const renderMobileMenu = () => (
    <>
      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black z-[199] transition-opacity duration-300 ${
          state.mobileMenuOpen ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => updateState({ mobileMenuOpen: false })}
      />

      {/* Mobile menu panel */}
      <div
        className={`fixed inset-y-0 right-0 w-full sm:w-80 z-[200] bg-gradient-to-br from-white to-purple-50 transform transition-transform duration-300 ease-in-out shadow-xl ${
          state.mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex justify-between items-center p-6 bg-gradient-to-r from-purple-700 to-purple-900 shadow-md">
            <Link to="/" className="text-2xl font-bold text-white" onClick={() => updateState({ mobileMenuOpen: false })}>
              PathFinder
            </Link>
            <button
              onClick={() => updateState({ mobileMenuOpen: false })}
              className="p-2 rounded-full bg-purple-800 hover:bg-purple-900 transition-colors"
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            <div className="space-y-6">
              <div className="pb-4 mb-2 border-b border-purple-200">
                <h3 className="text-sm uppercase tracking-wider text-purple-800 font-semibold mb-4">Navigation</h3>
                <Button
                  onClick={(e) => {
                    updateState({ mobileMenuOpen: false });
                    // if (!state.isAuthenticated) {
                    //   e.preventDefault();
                    //   handlers.toggleModal('showSignup', true);
                    // } else {
                    navigate('/features-page');
                    // }
                  }}
                  className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-100 rounded-lg flex items-center"
                >
                  <span className="bg-purple-100 p-2 rounded-lg mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </span>
                  Features Overview
                </Button>
              </div>

              {Object.entries(menuItems).map(([label, items]) => (
                <div key={label} className="space-y-3 pb-4 mb-2 border-b border-purple-200">
                  <div className="font-medium text-lg text-purple-900 px-4">{label}</div>
                  <div className="pl-4 space-y-2">
                    {items.map((item) => (
                      <Button
                        key={item.label}
                        onClick={() => {
                          updateState({ mobileMenuOpen: false });
                          if (!state.isAuthenticated) {
                            handlers.toggleModal('showSignup', true);
                          } else if (!item.isDisabled) {
                            navigate(item.path);
                          }
                        }}
                        className={`w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-100 rounded-lg flex items-center transition-colors
                          ${!state.isAuthenticated || item.isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className="bg-purple-100 p-2 rounded-lg mr-3">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 text-purple-700"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </span>
                        {item.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}

              <div className="space-y-3 pb-4 mb-2 border-b border-purple-200">
                <Button
                  onClick={(e) => {
                    updateState({ mobileMenuOpen: false });
                    // if (!state.isAuthenticated) {
                    //   e.preventDefault();
                    //   handlers.toggleModal('showSignup', true);
                    // } else {
                    navigate('/PathFinderCustomCheckout');
                    // }
                  }}
                  className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-100 rounded-lg flex items-center"
                >
                  <span className="bg-purple-100 p-2 rounded-lg mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  Pricing
                </Button>

                <Button
                  onClick={(e) => {
                    updateState({ mobileMenuOpen: false });
                    navigate('/blog');
                  }}
                  className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-100 rounded-lg flex items-center"
                >
                  <span className="bg-purple-100 p-2 rounded-lg mr-3">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M2 5a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 002 2H4a2 2 0 01-2-2V5zm3 1h6v4H5V6zm6 6H5v2h6v-2z"
                        clipRule="evenodd"
                      />
                      <path d="M15 7h1a2 2 0 012 2v5.5a1.5 1.5 0 01-3 0V7z" />
                    </svg>
                  </span>
                  Blog
                </Button>
                {state.isAuthenticated && userDetails?.role?.toLowerCase() === 'admin' && (
                  <>
                    <Button
                      onClick={() => {
                        updateState({ mobileMenuOpen: false });
                        navigate('/BlogManagementDashboard');
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-100 rounded-lg flex items-center"
                    >
                      <span className="bg-purple-100 p-2 rounded-lg mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                        </svg>
                      </span>
                      Blog Admin
                    </Button>
                    <Button
                      onClick={() => {
                        updateState({ mobileMenuOpen: false });
                        navigate('/CampaignManagementSystem');
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-100 rounded-lg flex items-center"
                    >
                      <span className="bg-purple-100 p-2 rounded-lg mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm0 2h12v10H4V5zm2 2v2h2V7H6zm0 4v2h2v-2H6zm4-4v2h2V7h-2zm0 4v2h2v-2h-2z" />
                        </svg>
                      </span>
                      Campaign Management System
                    </Button>
                    <Button
                      onClick={() => {
                        updateState({ mobileMenuOpen: false });
                        navigate('/PathFinderAnalytics');
                      }}
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-purple-100 rounded-lg flex items-center"
                    >
                      <span className="bg-purple-100 p-2 rounded-lg mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple-700" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      PathFinder Analytics
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="p-6 bg-gradient-to-r from-purple-50 to-purple-100">
            {state.isAuthenticated ? (
              <div className="flex flex-col space-y-4">
                <Button
                  onClick={() => {
                    updateState({ mobileMenuOpen: false });
                    handlers.toggleModal('showLogoutModal', true);
                  }}
                  className="w-full bg-gradient-to-r from-purple-700 to-purple-900 hover:from-purple-800 hover:to-purple-950 text-white px-6 py-3 rounded-lg text-center font-medium shadow-md transition-all"
                >
                  Sign Out
                </Button>
              </div>
            ) : (
              <div className="flex flex-col space-y-4">
                <Button
                  className="w-full px-6 py-3 border border-purple-300 text-purple-800 rounded-lg hover:bg-purple-100 text-center font-medium transition-colors"
                  onClick={() => {
                    updateState({ mobileMenuOpen: false });
                    handlers.toggleModal('showSignin', true);
                  }}
                >
                  Sign in
                </Button>
                <Button
                  className="w-full px-6 py-3 bg-gradient-to-r from-purple-700 to-purple-900 hover:from-purple-800 hover:to-purple-950 text-white rounded-lg shadow-md hover:shadow-lg text-center font-medium transition-all"
                  onClick={() => {
                    updateState({ mobileMenuOpen: false });
                    handlers.toggleModal('showSignup', true);
                  }}
                >
                  Get Started Free
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <Modal isOpen={state.showSignup} onClose={() => handlers.toggleModal('showSignup', false)}>
        <SignupPage onClose={() => handlers.toggleModal('showSignup', false)} />
      </Modal>

      <Modal isOpen={state.showSignin} onClose={() => handlers.toggleModal('showSignin', false)}>
        <SigninPage
          onClose={() => handlers.toggleModal('showSignin', false)}
          onForgotOpen={() => handlers.toggleModal('showForgotPass', true)}
        />
      </Modal>

      <Modal isOpen={state.showForgotPass} onClose={() => handlers.toggleModal('showForgotPass', false)}>
        <ForgotPassword onClose={() => handlers.toggleModal('showForgotPass', false)} />
      </Modal>

      <LogoutModal
        isOpen={state.showLogoutModal}
        onClose={() => handlers.toggleModal('showLogoutModal', false)}
        onConfirm={handlers.logout}
      />

      {!isHomePage && <NewFeatureBanner isVisible={state.showNewFeatureBanner} onDismiss={handlers.dismissFeatureBanner} navigate={navigate} />}

      {renderMobileMenu()}

      <nav
        className={`fixed w-full z-[100] transition-all duration-300 ${
          isHomePage
            ? (state.scrolled ? 'top-0 bg-purple-100 shadow-lg py-3' : 'top-0 bg-white py-5')
            : `${state.scrolled ? 'bg-purple-100 shadow-lg py-2' : 'bg-transparent py-3'} ${state.showNewFeatureBanner ? 'top-[120px] lg:top-[120px]' : 'lg:top-[65px]'}`
        }`}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-8">
              {isHomePage ? (
                <div
                  className="text-2xl font-bold bg-gradient-to-r from-purple-800 to-orange-600 bg-clip-text text-transparent cursor-pointer"
                  onClick={() => navigate('/')}
                >
                  PathFinder
                </div>
              ) : (
                <img
                  src="/PathFinderLogoBlackBackgroundPNG.png"
                  alt="PathFinder"
                  className="h-14 w-auto cursor-pointer -ml-2"
                  onClick={() => navigate('/')}
                />
              )}
              <div className="hidden lg:flex space-x-6 items-center">
                {!isHomePage && (
                <Button
                  onClick={(e) => {
                    // if (!state.isAuthenticated) {
                    //   e.preventDefault();
                    //   handlers.toggleModal('showSignup', true);
                    // } else {
                    navigate('/features-page');
                    // }
                  }}
                  className="text-gray-600 hover:text-purple-900 font-medium px-4 py-2"
                >
                  Features Overview
                </Button>
                )}

                {Object.entries(menuItems).map(([label, items]) => (
                  <div
                    key={label}
                    className="relative"
                    onMouseEnter={() => {
                      if (state.isAuthenticated) {
                        updateState({ activeDropdown: label });
                      }
                    }}
                    onMouseLeave={() => {
                      updateState({
                        activeDropdown: null,
                        activeSubmenu: null,
                        activeSubSubmenu: null
                      });
                    }}
                  >
                    <button
                      className="px-4 py-2 hover:text-blue-600 flex items-center"
                      onClick={(e) => {
                        if (!state.isAuthenticated) {
                          e.preventDefault();
                          handlers.toggleModal('showSignup', true);
                        }
                      }}
                    >
                      {label}
                      {items.length > 0 && <ChevronDown className="ml-1 w-4 h-4" />}
                    </button>
                    {items.length > 0 && state.activeDropdown === label && renderSubMenu(items)}
                  </div>
                ))}
                <Button
                  onClick={(e) => {
                    // if (!state.isAuthenticated) {
                    //   e.preventDefault();
                    //   handlers.toggleModal('showSignup', true);
                    // } else {
                    navigate('/PathFinderCustomCheckout');
                    // }
                  }}
                  className="text-gray-600 hover:text-purple-900 font-medium px-4 py-2"
                >
                  Pricing
                </Button>

                {!isHomePage && (
                <Button
                  onClick={(e) => {
                    if (!state.isAuthenticated) {
                      e.preventDefault();
                      navigate('/blog');
                    } else {
                      navigate('/blog');
                    }
                  }}
                  className="text-gray-600 hover:text-purple-900 font-medium px-4 py-2"
                >
                  Blog
                </Button>
                )}

                {/* {userDetails?.subscription_status === 'free' && (
                  <Button
                    onClick={(e) => {
                      if (!state.isAuthenticated) {
                        e.preventDefault();
                        handlers.toggleModal('showSignup', true);
                      } else {
                        navigate('/BusinessIdeaDevelopmentEngine');
                      }
                    }}
                    className="text-gray-600 hover:text-purple-900 font-medium px-4 py-2"
                  >
                    Business Idea Development Engine
                  </Button>
                )} */}

                {/* {state.isAuthenticated && userDetails?.role?.toLowerCase() === 'admin' && (
                  <Button
                    onClick={() => navigate('/BlogManagementDashboard')}
                    className="text-gray-600 hover:text-purple-900 font-medium px-4 py-2"
                  >
                    Blog Admin
                  </Button>
                )} */}

                {state.isAuthenticated && (
                  <Button
                    onClick={() => navigate('/AffiliateSignup')}
                    className="text-gray-600 hover:text-purple-900 font-medium px-4 py-2"
                  >
                    Affiliate
                  </Button>
                )}

                {state.isAuthenticated && (
                  <div
                    className="relative"
                    onMouseEnter={() => {
                      updateState({ activeDropdown: 'Sale Rep.' });
                    }}
                    onMouseLeave={() => {
                      updateState({
                        activeDropdown: null,
                        activeSubmenu: null,
                        activeSubSubmenu: null
                      });
                    }}
                  >
                    <button className="px-4 py-2 text-gray-600 hover:text-purple-900 font-medium flex items-center">
                      Sale Rep.
                      <ChevronDown className="ml-1 w-4 h-4" />
                    </button>
                    {state.activeDropdown === 'Sale Rep.' &&
                      renderSubMenu([
                        {
                          label: 'India',
                          subItems: [
                            { label: 'Hands On Training', path: '/WebBuilderLabs', isDisabled: false },
                            { label: 'Onboarding', path: '/WebBuilderSalesOnboarding', isDisabled: false },
                            {
                              label: 'Post Demo FollowUp',
                              path: '/PostDemoFollowUp',
                              isDisabled: false
                            },
                            { label: 'Sales Certification', path: '/PathFinderTrainingPortal', isDisabled: false },
                            { label: 'Script Generator', path: '/WebBuilderScriptGenerator', isDisabled: false }
                          ]
                        }
                      ])}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <button
                className="lg:hidden p-2.5 rounded-full bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 shadow-md transition-all"
                onClick={() => updateState({ mobileMenuOpen: true })}
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5 text-white" />
              </button>
              <div className="hidden lg:block">
                {state.isAuthenticated ? (
                  <div className="flex items-center space-x-4">
                    <Button
                      onClick={() => handlers.toggleModal('showLogoutModal', true)}
                      className="bg-[#8F3BF4] hover:bg-[#7C2FD4] text-white px-6 py-2 rounded-lg"
                    >
                      Sign Out
                    </Button>
                    <ProfileCircle />
                  </div>
                ) : (
                  <>
                    <Button
                      className="text-gray-600 hover:text-purple-900 font-medium mx-4"
                      onClick={() => handlers.toggleModal('showSignin', true)}
                    >
                      Sign in
                    </Button>
                    <Button
                      className="px-6 py-2.5 bg-gradient-to-r from-purple-800 to-purple-900 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 shadow-md hover:shadow-lg"
                      onClick={() => handlers.toggleModal('showSignup', true)}
                    >
                      Get Started Free
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>
      <div className={isHomePage ? 'h-20' : `h-20 ${state.showNewFeatureBanner ? 'h-[100px] sm:h-[120px] lg:h-[140px]' : ''}`} />
    </>
  );
};

export default Navbar;
